/**
 * Railit Button — a thin adapter over shadcn's Button.
 *
 * The export signature is unchanged on purpose (`primary | ghost | danger`,
 * `sm | md`, `leadingIcon` / `trailingIcon`), so the ~90 pages that already
 * import it keep compiling while they are migrated one at a time. New code can
 * reach for `@/components/shadcn/button` directly.
 */

import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Button as ShadcnButton } from '@/components/shadcn/button';
import { cn } from '@/lib/utils';

export type ButtonVariant = 'primary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

/**
 * `primary` means "the main action on this screen" — Save, Post, Create — which
 * is exactly what the yellow is for, so it maps to `action` rather than to the
 * blue `default`. Mapping it here rather than editing several hundred call
 * sites is the point of this adapter existing: the whole app changed with one
 * line, and a page that wants the blue button can still ask for it directly
 * from components/shadcn/button.
 */
const VARIANT_MAP = {
  primary: 'action',
  ghost: 'outline',
  danger: 'destructive',
} as const;

const SIZE_MAP = {
  sm: 'sm',
  md: 'default',
} as const;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  /** Render as the single child element (e.g. a react-router <Link>). */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    className,
    children,
    leadingIcon,
    trailingIcon,
    type,
    asChild,
    ...rest
  },
  ref,
) {
  return (
    <ShadcnButton
      ref={ref}
      type={type ?? 'button'}
      variant={VARIANT_MAP[variant]}
      size={SIZE_MAP[size]}
      asChild={asChild}
      className={cn('[&_svg]:size-4', className)}
      {...rest}
    >
      {leadingIcon}
      {children}
      {trailingIcon}
    </ShadcnButton>
  );
});
